import type { GeoEvent, NewsItem, ThreatLevel, EventType } from '@/types';
import { API_ENDPOINTS } from '@/config/data-sources';
import { hashString } from '@/utils/format';
import { stripHtml } from '@/utils/sanitize';

// ---------------------------------------------------------------------------
// GDELT Project v2 — GEO API (point data) + DOC API (article list)
// ---------------------------------------------------------------------------

const TIMEOUT_MS = 20_000;

// In dev, route through Vite proxy to avoid CORS. In prod, use direct URL.
const GDELT_BASE = import.meta.env.DEV
  ? '/gdelt-proxy/api/v2'
  : 'https://api.gdeltproject.org/api/v2';

// ===== GEO API types =====

interface GdeltGeoFeature {
  type: string;
  geometry: {
    type: string;
    coordinates: [number, number]; // [lon, lat]
  };
  properties: {
    name?: string;
    html?: string;
    url?: string;
    urlmobile?: string;
    domain?: string;
    tone?: number;
    goldsteinscale?: number;
    shareimage?: string;
    language?: string;
    sourcelanguage?: string;
    contextualtext?: string;
  };
}

interface GdeltGeoResponse {
  type: string;
  features: GdeltGeoFeature[];
}

// ===== DOC API types =====

interface GdeltArticle {
  url: string;
  url_mobile?: string;
  title: string;
  seendate: string; // "20240315T120000Z"
  socialimage?: string;
  domain: string;
  language: string;
  sourcecountry?: string;
  tone?: number;
}

interface GdeltDocResponse {
  articles: GdeltArticle[];
}

// ===== Event type keyword map =====

const EVENT_TYPE_KEYWORDS: [RegExp, EventType][] = [
  [/\b(military|armed forces|troops|defense|weapon|missile|navy|army|air.?force)\b/i, 'military'],
  [/\b(conflict|war|fighting|battle|clash|strike|bombing|airstrike|shelling)\b/i, 'conflict'],
  [/\b(protest|demonstrat|rally|riot|unrest|uprising|march)\b/i, 'protest'],
  [/\b(terror|attack|bomb|explosion|isis|al.?qaeda|militant)\b/i, 'terrorism'],
  [/\b(cyber|hack|breach|ransomware|malware|phishing)\b/i, 'cyber'],
  [/\b(earthquake|tsunami|flood|hurricane|typhoon|tornado|volcano|landslide)\b/i, 'disaster'],
  [/\b(economic|trade|tariff|sanction|market|gdp|inflation|recession)\b/i, 'economic'],
  [/\b(diplomat|embassy|summit|treaty|negotiat|peace.?talk)\b/i, 'diplomatic'],
  [/\b(pandemic|outbreak|epidemic|virus|disease|health)\b/i, 'health'],
  [/\b(infrastructure|pipeline|dam|power.?grid|blackout|supply.?chain)\b/i, 'infrastructure'],
];

// ---------------------------------------------------------------------------
// GEO Events (conflict/protest point data)
// ---------------------------------------------------------------------------

/**
 * Fetch geolocated events from GDELT GEO API.
 * Query covers conflict, protest, and military keywords over last 24h.
 */
export async function fetchGdeltEvents(): Promise<GeoEvent[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  const params = new URLSearchParams({
    query: 'conflict OR protest OR military',
    mode: 'PointData',
    format: 'GeoJSON',
    timespan: '1440', // last 24h in minutes
  });

  // GDELT GEO /api/v2/geo/geo endpoint is deprecated (returns 404).
  // Return empty array silently.
  clearTimeout(timer);
  return [];
}

function parseGeoFeatures(features: GdeltGeoFeature[]): GeoEvent[] {
  if (!Array.isArray(features)) return [];

  return features
    .filter((f) => f.geometry?.coordinates?.length === 2)
    .map((f): GeoEvent => {
      const [lon, lat] = f.geometry.coordinates;
      const p = f.properties;

      // Extract text from the HTML snippet for title / description
      const rawText = p.contextualtext ?? p.html ?? p.name ?? '';
      const cleanText = stripHtml(rawText).slice(0, 500);
      const title = cleanText.slice(0, 120) || 'GDELT Event';

      const tone = p.tone ?? 0;
      const severity = classifySeverity(tone, p.goldsteinscale);
      const eventType = inferEventType(cleanText);

      return {
        id: `gdelt-geo-${hashString(`${lon}${lat}${title}`)}`,
        type: eventType,
        title,
        description: cleanText.length > 120 ? cleanText : undefined,
        lat,
        lon,
        timestamp: new Date().toISOString(), // GDELT GEO doesn't expose exact time per point
        source: p.domain ?? 'GDELT',
        severity,
        url: p.url ?? p.urlmobile ?? undefined,
        category: eventType,
      };
    });
}

/**
 * Classify threat severity from GDELT tone and Goldstein scale.
 * Tone: ranges roughly -100 to +100 (negative = bad news).
 * Goldstein: -10 (extreme conflict) to +10 (extreme cooperation).
 */
function classifySeverity(tone: number, goldstein?: number): ThreatLevel {
  const g = goldstein ?? 0;

  if (tone < -8 || g < -7) return 'critical';
  if (tone < -5 || g < -4) return 'high';
  if (tone < -2 || g < -1) return 'medium';
  if (tone < 0 || g < 0) return 'low';
  return 'info';
}

function inferEventType(text: string): EventType {
  for (const [pattern, type] of EVENT_TYPE_KEYWORDS) {
    if (pattern.test(text)) return type;
  }
  return 'conflict'; // default for GDELT conflict/protest query
}

// ---------------------------------------------------------------------------
// DOC API (article list for news feed)
// ---------------------------------------------------------------------------

/**
 * Fetch recent geopolitical articles from GDELT DOC API.
 */
export async function fetchGdeltNews(): Promise<NewsItem[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  const params = new URLSearchParams({
    query: 'geopolitical OR conflict OR military OR Iran OR missile OR attack',
    mode: 'ArtList',
    format: 'json',
    maxrecords: '75',
    timespan: '4320', // last 72h (3 days) — catches events from a few days ago
  });

  try {
    const res = await fetch(`${GDELT_BASE}/doc/doc?${params}`, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`GDELT DOC responded with ${res.status}`);
    }

    const data: GdeltDocResponse = await res.json();
    return parseArticles(data.articles ?? []);
  } catch (err) {
    console.error('[GDELT-DOC] Failed to fetch news', err);
    return [];
  } finally {
    clearTimeout(timer);
  }
}

function parseArticles(articles: GdeltArticle[]): NewsItem[] {
  if (!Array.isArray(articles)) return [];

  return articles.map((a): NewsItem => {
    const publishedAt = parseGdeltDate(a.seendate);
    const tone = a.tone ?? 0;
    const severity = classifySeverityFromTone(tone);
    const category = inferEventType(a.title);

    return {
      id: `gdelt-doc-${hashString(a.url)}`,
      title: stripHtml(a.title),
      source: a.domain,
      url: a.url,
      publishedAt,
      severity,
      category,
      country: a.sourcecountry ?? undefined,
      imageUrl: a.socialimage ?? undefined,
    };
  });
}

/**
 * Parse GDELT date format "20240315T120000Z" into ISO string.
 */
function parseGdeltDate(raw: string): string {
  if (!raw) return new Date().toISOString();
  try {
    // Format: "20240315T120000Z"
    const year = raw.slice(0, 4);
    const month = raw.slice(4, 6);
    const day = raw.slice(6, 8);
    const hour = raw.slice(9, 11);
    const min = raw.slice(11, 13);
    const sec = raw.slice(13, 15);
    return new Date(`${year}-${month}-${day}T${hour}:${min}:${sec}Z`).toISOString();
  } catch {
    return new Date().toISOString();
  }
}

function classifySeverityFromTone(tone: number): ThreatLevel {
  if (tone < -8) return 'critical';
  if (tone < -5) return 'high';
  if (tone < -2) return 'medium';
  if (tone < 0) return 'low';
  return 'info';
}
